import { useOutletContext } from "react-router-dom";
import { DateTime } from "luxon";
import theme from "../../../../theme";
import { Loader } from "../../../../components/Loader";
import { CardLine } from "../../../IndividualView";
import { Box } from "../../../../components/Box";
import { useFillViewportHeight } from "../../../../hooks/useFillViewportHeight";
import { Flex } from "../../../../components/Flex";
import { Paragraph } from "../../../../components/Paragraph";
import { useProfileMonitoring } from "../../../../data/useProfileMonitoring";
import { Button } from "../../../../components/Button";
import { Image } from "../../../../components/Image";
import success from "../../../../assets/card-success-icon.svg";
import inReview from "../../../../assets/card-in-review-icon.svg";
import pending from "../../../../assets/pending-icon.svg";
import { Profile } from "../../../../types/Profile";

const formatDate = (iso?: string | null) =>
  iso ? DateTime.fromISO(iso).toFormat("dd/MM/yyyy") : "-";

const getDisplayName = (item: any) => {
  if (item.company_name) return item.company_name;

  const parts = [
    item.title,
    item.first_name,
    item.middle_name,
    item.last_name,
  ].filter(Boolean);

  if (parts.length) return parts.join(" ");

  return item.reference ?? "-";
};

const monitoringIcons: Record<string, string> = {
  active: success,
  "requires review": inReview,
  pending: pending,
};

export const MonitoringTab = ({
  profile: profileOverride,
}: {
  profile?: Profile;
}) => {
  const { ref: fillRef, height: fillHeight } = useFillViewportHeight();
  const outlet = useOutletContext<{ profile: Profile } | null>();
  const profile = profileOverride ?? outlet?.profile;
  const { data, isLoading, isError } = useProfileMonitoring(profile?.id);

  if (isLoading) return <Loader />;
  if (isError) return <CardLine>Failed to load monitoring.</CardLine>;

  const monitoringList = Array.isArray(data?.results) ? data.results : [];

  const getMonitoringViewUrl = (item: any) => {
    return `/monitoring/${item.id}`;
  };

  return (
    <Box ref={fillRef} height={fillHeight} overflowY="auto" overflowX="hidden">
      {monitoringList.length ? (
        <Box
          background={theme.colors.white}
          borderRadius={12}
          border={`1px solid ${theme.colors.divider}`}
          overflow="hidden"
        >
          {/* Header row */}
          <Flex
            justifyContent="space-between"
            alignItems="center"
            p={14}
            borderBottom={`1px solid ${theme.colors.divider}`}
            background="#F9FAFB"
          >
            <Paragraph my={0} fontWeight={600} width="22%" color="gray.60">
              Name
            </Paragraph>
            <Paragraph my={0} fontWeight={600} width="20%" color="gray.60">
              Reference
            </Paragraph>
            <Paragraph my={0} fontWeight={600} width="16%" color="gray.60">
              Status
            </Paragraph>
            <Paragraph my={0} fontWeight={600} width="16%" color="gray.60">
              Created
            </Paragraph>
            <Paragraph my={0} fontWeight={600} width="16%" color="gray.60">
              Last activity
            </Paragraph>
            <Box width="10%" />
          </Flex>

          {/* Data rows */}
          {monitoringList.map((item: any) => {
            const name = getDisplayName(item);
            const reference = item.reference ?? "-";
            const statusLabel = item?.status?.label ?? "-";
            const createdOn = formatDate(item.created_on);
            const lastActivity = formatDate(item.last_activity);

            return (
              <Flex
                key={item.id}
                justifyContent="space-between"
                alignItems="center"
                p={14}
                borderBottom={`1px solid ${theme.colors.divider}`}
              >
                <Paragraph fontWeight={600} my={0} width="22%">
                  {name}
                </Paragraph>

                <Paragraph my={0} width="20%" color="gray.60">
                  {reference}
                </Paragraph>

                <Flex alignItems="center" width="16%" color="gray.60">
                  {item?.status?.status &&
                    monitoringIcons[item.status.status] && (
                      <Image
                        src={monitoringIcons[item.status.status]}
                        alt={item.status.status}
                        width="22px"
                        mr={1}
                      />
                    )}
                  <Paragraph my={0}>{statusLabel}</Paragraph>
                </Flex>

                <Paragraph color="gray.60" my={0} width="16%">
                  {createdOn}
                </Paragraph>

                <Paragraph color="gray.60" my={0} width="16%">
                  {lastActivity}
                </Paragraph>

                <Box
                  width="10%"
                  display="flex"
                  justifyContent="flex-end"
                >
                  <Button
                    variant="primary"
                    isDisabled={!item.pdf}
                    onClick={() =>
                      window.open(getMonitoringViewUrl(item), "_blank")
                    }
                  >
                    View
                  </Button>
                </Box>
              </Flex>
            );
          })}
        </Box>
      ) : (
        <CardLine>No monitoring found.</CardLine>
      )}
    </Box>
  );
};
